import { Fragment, useEffect, useMemo, useState } from 'react';
import { Icoon } from '../ui/Icoon';
import { Link, useParams } from 'react-router-dom';
import {
  ITEM_STATUS_LABEL,
  addDagen,
  agileProjectPrognoses,
  agileUren,
  combineerPrognose,
  fmtDag,
  fmtUren,
  geplandeUren,
  isoWeek,
  maandagVan,
  projectPrognose,
  verschilDagen,
  werkpakketVoortgang,
  type PakketStatus,
} from '@ep/core';
import { PROJECT_STATUS, useData, useIk, type TaakRij, type TakenpakketRij, type WerkpakketRij } from '../lib/data';
import { urenVan } from '../ui/TakenTabel';
import { useBaselines, useProjectStand } from '../ui/Baseline';
import { Laden } from '../ui/ui';

/** SPEC §4.16 – statusrapport van een project voor de projectmanager (scherm en A4). */
export function ProjectRapport() {
  const { id } = useParams();
  const { data: d, kal } = useData();
  const hs = useIk().data?.organisatie?.huisstijl;
  const [open, setOpen] = useState<Set<string>>(new Set());
  const p = d?.projecten.find((x) => x.id === id);
  const vd = d?.vandaag ?? '';
  // SPEC §20: vergelijking met de nieuwste baseline
  const bl = useBaselines(id).data?.[0] ?? null;
  const stand = useProjectStand(d, id, bl);

  // afdrukken: alles openklappen
  useEffect(() => {
    const voor = () => setOpen(new Set(['*']));
    window.addEventListener('beforeprint', voor);
    return () => window.removeEventListener('beforeprint', voor);
  }, []);

  const r = useMemo(() => {
    if (!d || !p || !kal) return null;
    const taken = d.taken.filter((t) => t.projectId === p.id && t.taakType !== 'FOCUS');
    const wps = d.werkpakketten.filter((w) => w.projectId === p.id).sort((a, b) => a.volgorde - b.volgorde);
    const tps = d.takenpakketten.filter((t) => t.projectId === p.id);
    const budget = p.urenBudget ?? p.expertiseBudget.reduce((s, b) => s + b.uren, 0);
    const ag = agileUren(tps);
    const gepland = geplandeUren(taken) + ag.besteed + (p.besteedLosMin ?? 0) / 60;
    const klaarUren = geplandeUren(taken.filter((t) => t.terugmelding === 'KLAAR')) + ag.klaar;
    const prog = projectPrognose(kal, p, d.taken, vd, undefined, d.takenpakketten);
    const agile = agileProjectPrognoses({ kal, projectId: p.id, werkpakketten: d.werkpakketten, items: d.takenpakketten, sprints: d.sprints ?? [], vandaag: vd }).filter((x) => x.rest > 0);
    const totaal = combineerPrognose(prog.prognose, prog.laterDanHorizon, agile.map((x) => x.eind));
    const wpV = wps.map((w) => ({ w, v: werkpakketVoortgang(w, wps, tps, taken, vd) }));
    const wacht = taken.filter((t) => t.status === 'GEPLAND' && !t.terugmelding && t.taakType !== 'OVERLEG' && (t.einddatum ?? '') < vd);
    const nietKlaar = taken.filter((t) => (t.terugmelding === 'NIET_KLAAR' || t.terugmelding === 'HERPLANNEN') && !t.tmAfgehandeld);
    const team = new Set(taken.map((t) => t.engineerId).filter(Boolean) as string[]);
    for (const s of d.sprints ?? []) if (wps.some((w) => w.id === s.werkpakketId)) s.leden.forEach((l) => team.add(l.engineerId));

    // uren per week: 3 terug, deze week, 4 vooruit
    const ma = maandagVan(vd);
    const weken = Array.from({ length: 8 }, (_, i) => ({ start: addDagen(ma, (i - 3) * 7), geweest: 0, vandaag: 0, komt: 0 }));
    const eind = addDagen(weken[7].start, 6);
    for (const t of taken) {
      if (t.status !== 'GEPLAND' || !t.startdatum || !t.engineerId) continue;
      if ((t.einddatum ?? t.startdatum) < weken[0].start || t.startdatum > eind) continue;
      const e = kal.engineer(t.engineerId);
      if (!e) continue;
      for (const dag of kal.planDagen(e, t.startdatum, Math.max(1, t.dagen))) {
        const w = weken.find((x) => dag >= x.start && dag <= addDagen(x.start, 6));
        if (!w) continue;
        const u = kal.takenUrenOpDag(t, dag);
        if (dag < vd) w.geweest += u;
        else if (dag === vd) w.vandaag += u;
        else w.komt += u;
      }
    }

    // mijlpalen
    const mijl: { datum: string; titel: string; sub: string; soort: 'gewoon' | 'vandaag' | 'live' }[] = [];
    if (p.start) mijl.push({ datum: p.start, titel: 'Start', sub: 'Project gestart', soort: 'gewoon' });
    for (const { w, v } of wpV) if (w.deadline) mijl.push({ datum: w.deadline, titel: `${w.naam} gereed`, sub: v.label, soort: 'gewoon' });
    mijl.push({ datum: vd, titel: `Vandaag · week ${isoWeek(vd)}`, sub: ((n) => (n === 0 ? 'geen taken vandaag' : `${n} ${n === 1 ? 'taak' : 'taken'} vandaag`))(taken.filter((t) => t.status === 'GEPLAND' && (t.startdatum ?? '') <= vd && (t.einddatum ?? '') >= vd).length), soort: 'vandaag' });
    if (p.oplevering) mijl.push({ datum: p.oplevering, titel: 'Oplevering', sub: 'Project opgeleverd', soort: 'gewoon' });
    if (p.livegang) mijl.push({ datum: p.livegang, titel: 'Livegang', sub: 'In gebruik', soort: 'live' });
    mijl.sort((a, b) => a.datum.localeCompare(b.datum) || (a.soort === 'vandaag' ? -1 : 1));

    return { taken, wps, tps, budget, gepland, klaarUren, prog, agile, totaal, wpV, wacht, nietKlaar, team, weken, mijl };
  }, [d, p, kal, vd]);

  if (!d || !kal) return <Laden />;
  if (!p || !r) return <div className="pagina"><div className="paneel leeg">Project niet gevonden. <Link to="/projecten">Terug</Link></div></div>;

  const doel = p.livegang ?? p.oplevering;
  const doelNaam = p.livegang ? 'livegang' : 'oplevering';
  const dagen = doel ? verschilDagen(vd, doel) : null;
  const pct = r.budget > 0 ? Math.round((r.gepland / r.budget) * 100) : 0;
  const wpKlaar = r.wpV.filter((x) => x.v.status === 'Ingepland').length;
  const wpLaat = r.wpV.filter((x) => x.v.status === 'Te laat');
  const progDatum = r.totaal.datum;
  const progLaat = !!(progDatum && p.livegang && progDatum > p.livegang);
  const progVerschil = progDatum && p.livegang ? verschilDagen(p.livegang, progDatum) : 0;
  const naam = (eid?: string | null) => d.engineers.find((e) => e.id === eid)?.naam ?? '–';
  const kort = (eid?: string | null) => naam(eid).split(' ')[0];

  // aandachtspunten
  const punten: { toon: 'rood' | 'oranje'; titel: string; tekst: string }[] = [];
  if (progLaat) punten.push({ toon: 'rood', titel: 'Prognose na de livegang', tekst: `Het werk is naar verwachting op ${fmtDag(progDatum, 'jaar')} klaar, ${progVerschil} dag${progVerschil === 1 ? '' : 'en'} na de livegang.` });
  if (r.totaal.later) punten.push({ toon: 'oranje', titel: 'Prognose onbekend', tekst: 'Een agile werkpakket heeft nog geen sprint met team; daardoor is er nog geen prognose.' });
  if (wpLaat.length) punten.push({ toon: 'rood', titel: `${wpLaat.length} werkpakket${wpLaat.length === 1 ? '' : 'ten'} te laat`, tekst: wpLaat.map((x) => `${x.w.naam} (deadline ${fmtDag(x.w.deadline, 'datum')})`).join(', ') + ' is nog niet volledig ingepland.' });
  if (r.gepland > r.budget + 0.01 && r.budget > 0) punten.push({ toon: 'rood', titel: 'Budget overschreden', tekst: `Er is ${fmtUren(r.gepland - r.budget)} uur meer ingepland dan het budget van ${fmtUren(r.budget)} uur.` });
  for (const x of r.prog.perExpertise.filter((e) => e.haalbaar === false)) punten.push({ toon: 'oranje', titel: `${x.expertise} krap`, tekst: `Nog ${fmtUren(x.rest)} uur te doen, maar tot de livegang is er ${fmtUren(x.beschikbaar)} uur beschikbaar.` });
  if (r.wacht.length) punten.push({ toon: 'oranje', titel: `${r.wacht.length} taak${r.wacht.length === 1 ? '' : 'en'} wacht${r.wacht.length === 1 ? '' : 'en'} op terugmelding`, tekst: r.wacht.slice(0, 4).map((t) => `${t.titel} (${kort(t.engineerId)}, ${fmtDag(t.einddatum, 'datum')})`).join(', ') + (r.wacht.length > 4 ? ' en meer.' : '.') });
  if (r.nietKlaar.length) {
    const extra = r.nietKlaar.reduce((s, t) => s + (t.tmRestUren ?? 0), 0);
    punten.push({ toon: 'oranje', titel: 'Werk niet afgerond', tekst: `${r.nietKlaar.length} taak${r.nietKlaar.length === 1 ? ' is' : 'en zijn'} teruggemeld als niet klaar of herplannen${extra ? `; nog ${fmtUren(extra)} uur nodig` : ''}. Plan een vervolg.` });
  }

  const losse = r.taken.filter((t) => !t.werkpakketId && !r.tps.some((tp) => tp.id === t.takenpakketId && tp.werkpakketId));
  const groepen: { key: string; w: WerkpakketRij | null; status: PakketStatus | null; label: string; uren: number; klaar: number; taken: TaakRij[]; tps: TakenpakketRij[] }[] = [
    ...r.wpV.map(({ w, v }) => {
      const tps = r.tps.filter((t) => t.werkpakketId === w.id);
      const taken = r.taken.filter((t) => t.werkpakketId === w.id || tps.some((tp) => tp.id === t.takenpakketId));
      const klaar = w.werkwijze === 'AGILE' ? v.ingepland : geplandeUren(taken.filter((t) => t.terugmelding === 'KLAAR'));
      return { key: w.id, w, status: v.status, label: v.label, uren: v.uren, klaar, taken, tps };
    }),
    ...(losse.length ? [{ key: 'los', w: null, status: null, label: '', uren: geplandeUren(losse), klaar: geplandeUren(losse.filter((t) => t.terugmelding === 'KLAAR')), taken: losse, tps: [] }] : []),
  ];
  const allesOpen = open.has('*');
  const isOpen = (k: string) => allesOpen || open.has(k);
  const wissel = (k: string) => setOpen((o) => { const n = new Set(o); n.delete('*'); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const maxWeek = Math.max(1, ...r.weken.map((w) => w.geweest + w.vandaag + w.komt));

  const samenvatting = [
    doel && dagen != null ? (dagen > 0 ? `Over ${dagen} dag${dagen === 1 ? '' : 'en'} is de ${doelNaam} van ${p.naam}.` : dagen === 0 ? `Vandaag is de ${doelNaam} van ${p.naam}.` : `${p.naam} is ${-dagen} dag${dagen === -1 ? '' : 'en'} geleden live gegaan.`) : `${p.naam} heeft nog geen livegang.`,
    r.budget ? `${pct}% van het budget van ${fmtUren(r.budget)} uur is ingepland en ${fmtUren(r.klaarUren)} uur is als klaar teruggemeld.` : '',
    r.wps.length ? `${wpKlaar} van de ${r.wps.length} werkpakketten ${wpKlaar === 1 ? 'is' : 'zijn'} volledig ingepland of klaar.` : '',
    progDatum ? (progLaat ? `Volgens de prognose is het werk op ${fmtDag(progDatum, 'jaar')} klaar; dat is ${progVerschil} dag${progVerschil === 1 ? '' : 'en'} na de livegang.` : `Volgens de prognose is het werk op ${fmtDag(progDatum, 'jaar')} klaar${p.livegang ? ', op tijd voor de livegang' : ''}.`) : '',
    bl && stand?.verschil != null
      ? stand.verschil === 0
        ? `Het werk is klaar op dezelfde dag als in de baseline van ${new Date(bl.aangemaakt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' })}.`
        : `Ten opzichte van de baseline van ${new Date(bl.aangemaakt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' })} is het werk ${Math.abs(stand.verschil)} werkdag${Math.abs(stand.verschil) === 1 ? '' : 'en'} ${stand.verschil > 0 ? 'later' : 'eerder'} klaar.`
      : '',
  ].filter(Boolean);

  return (
    <div className="rapport">
      <div className="rap-acties geen-print">
        <Link className="knop" to={`/projecten/${p.id}`}><Icoon naam="arrow-left" maat={16} /> Terug naar het project</Link>
        <div className="groei" />
        <button className="knop" onClick={() => setOpen(allesOpen ? new Set() : new Set(['*']))}>{allesOpen ? 'Alles dichtklappen' : 'Alles openklappen'}</button>
        <button className="knop primair" onClick={() => window.print()}><Icoon naam="printer" maat={16} /> Afdrukken of PDF</button>
      </div>

      {/* KOP */}
      <header className="rap-kop">
        <div className="rap-kop-tekst">
          <div className="rap-kicker">{p.nummer} · Statusrapport</div>
          <h1>{p.naam}</h1>
          <p>{[p.klant, p.pmNaam ? `projectmanager ${p.pmNaam}` : null, PROJECT_STATUS[p.status]].filter(Boolean).join(' · ')}</p>
          <span className="rap-chip">Stand van zaken: {fmtDag(vd, 'lang')} {vd.slice(0, 4)} · week {isoWeek(vd)}</span>
        </div>
        <div className="rap-kop-rechts">
          {hs?.logoVersie ? <img src={`/api/huisstijl/logo?v=${hs.logoVersie}`} alt="" className="rap-logo eigen" /> : <img src="/logo.svg" alt="bsmartr" className="rap-logo" />}
          {doel && dagen != null && (
            <div className="rap-teller">
              <b>{Math.abs(dagen)}</b>
              <span>{dagen >= 0 ? `dagen tot ${doelNaam}` : `dagen sinds ${doelNaam}`} · {fmtDag(doel, 'lang')}</span>
            </div>
          )}
        </div>
      </header>

      {/* BIJPRATEN */}
      <section className="rap-blok rap-bijpraten">
        <div className="rap-label">Even bijpraten</div>
        <p>{samenvatting.map((z, i) => (i === 0 ? <b key={i}>{z} </b> : <Fragment key={i}>{z} </Fragment>))}</p>
        <p
          className="rap-notitie"
          contentEditable
          suppressContentEditableWarning
          // geplakte tekst zonder opmaak, zodat het lettertype gelijk blijft aan de rest
          onPaste={(e) => {
            e.preventDefault();
            const t = e.clipboardData.getData('text/plain');
            const sel = window.getSelection();
            if (!sel?.rangeCount) return;
            const r = sel.getRangeAt(0);
            r.deleteContents();
            r.insertNode(document.createTextNode(t));
            r.collapse(false);
            sel.removeAllRanges();
            sel.addRange(r);
          }}
          data-leeg="Typ hier een eigen toelichting voor dit rapport (wordt niet bewaard)." />
      </section>

      {/* KERNCIJFERS */}
      <div className="rap-kpis">
        <Kpi label="Urenbudget" getal={fmtUren(r.budget)} eenheid="uur" tekst={`${r.prog.perExpertise.length} expertise${r.prog.perExpertise.length === 1 ? '' : 's'} · ${r.team.size} engineer${r.team.size === 1 ? '' : 's'} in het team`} kleur="navy" />
        <Kpi label="Ingepland" getal={fmtUren(r.gepland)} eenheid={`van ${fmtUren(r.budget)} uur`} tekst={`${fmtUren(r.klaarUren)} uur klaar teruggemeld · ${pct}% van het budget`} balk={[r.klaarUren, r.gepland, r.budget]} kleur="oranje" />
        <Kpi label="Werkpakketten klaar" getal={String(wpKlaar)} eenheid={`van ${r.wps.length}`} tekst={wpLaat.length ? `${wpLaat.length} te laat` : 'geen werkpakket te laat'} balk={[wpKlaar, wpKlaar, Math.max(1, r.wps.length)]} kleur="groen" rood={wpLaat.length > 0} />
        <Kpi
          label="Prognose"
          getal={progDatum ? fmtDag(progDatum, 'datum') : r.totaal.later ? '?' : '✓'}
          eenheid={progDatum ? progDatum.slice(0, 4) : ''}
          tekst={progDatum ? (p.livegang ? (progLaat ? `${progVerschil} dagen na de livegang` : `op tijd · livegang ${fmtDag(p.livegang, 'datum')}`) : 'verwacht klaar') : r.totaal.later ? 'nog geen sprint met team' : 'alles is ingepland'}
          kleur={progLaat ? 'rood' : 'navy'}
          rood={progLaat}
        />
      </div>

      {/* 01 MIJLPALEN */}
      <Kop nr="01" titel={`Onderweg naar de ${doelNaam}`} />
      <section className="rap-blok rap-mijlpalen">
        <div className="rap-lijn" />
        {r.mijl.map((m, i) => (
          <div key={i} className={`rap-mijl ${m.soort} ${m.datum < vd && m.soort !== 'vandaag' ? 'geweest' : ''}`}>
            <i />
            <b>{m.soort === 'vandaag' ? fmtDag(m.datum, 'datum') : fmtDag(m.datum, 'datum')}</b>
            <strong>{m.titel}</strong>
            <span>{m.sub}</span>
          </div>
        ))}
      </section>

      {/* 02 WERKPAKKETTEN */}
      <Kop nr="02" titel="Voortgang per werkpakket" />
      <section className="rap-blok rap-tabel">
        <div className="rap-tabel-kop geen-print">Klik op een werkpakket om de taken te zien</div>
        <table>
          <thead>
            <tr><th>Werkpakket</th><th>Werkwijze</th><th>Team</th><th>Uren</th><th>Status</th><th>Volgende</th><th className="geen-print" /></tr>
          </thead>
          <tbody>
            {groepen.map((g) => {
              const agileWp = g.w?.werkwijze === 'AGILE';
              const tm = new Set<string>(g.taken.map((t) => t.engineerId).filter(Boolean) as string[]);
              if (agileWp) for (const s of d.sprints ?? []) if (s.werkpakketId === g.w!.id && s.status !== 'AFGESLOTEN') s.leden.forEach((l) => tm.add(l.engineerId));
              const ingepland = agileWp ? (g.tps.reduce((s, t) => s + (t.besteedMin ?? 0), 0) / 60) : geplandeUren(g.taken);
              const volgendeTaak = g.taken.filter((t) => t.status === 'GEPLAND' && (t.einddatum ?? '') >= vd).sort((a, b) => (a.startdatum ?? '').localeCompare(b.startdatum ?? ''))[0];
              const sprint = agileWp ? (d.sprints ?? []).filter((s) => s.werkpakketId === g.w!.id && s.status !== 'AFGESLOTEN').sort((a, b) => a.start.localeCompare(b.start))[0] : undefined;
              const volgende = sprint ? `Sprint ${sprint.nummer} ${sprint.status === 'LOPEND' ? `tot ${fmtDag(sprint.eind, 'datum')}` : `vanaf ${fmtDag(sprint.start, 'datum')}`}` : volgendeTaak ? ((volgendeTaak.startdatum ?? '') <= vd ? 'Vandaag' : fmtDag(volgendeTaak.startdatum, 'kort')) : g.w?.deadline ? `deadline ${fmtDag(g.w.deadline, 'datum')}` : '–';
              const bezigVandaag = g.taken.some((t) => t.status === 'GEPLAND' && (t.startdatum ?? '') <= vd && (t.einddatum ?? '') >= vd);
              const max = Math.max(g.uren, ingepland, 0.01);
              return (
                <Fragment key={g.key}>
                  <tr className="rap-rij" onClick={() => wissel(g.key)}>
                    <td>
                      <b>{g.w ? g.w.naam : 'Direct onder het project'}</b>
                      <small>{g.w?.deadline ? `deadline ${fmtDag(g.w.deadline, 'jaar')}` : 'losse taken'}{g.w?.afhankelijkVanId ? ` · na ${r.wps.find((x) => x.id === g.w!.afhankelijkVanId)?.naam ?? ''}` : ''}</small>
                    </td>
                    <td><span className={`rap-pil ${agileWp ? 'agile' : 'klassiek'}`}>{g.w ? (agileWp ? 'Agile' : 'Klassiek') : 'Los'}</span></td>
                    <td><b>{tm.size}</b><small>{[...tm].slice(0, 3).map(kort).join(', ')}{tm.size > 3 ? ' e.a.' : ''}</small></td>
                    <td className="rap-uren">
                      <div className="rap-balk"><i className="klaar" style={{ width: `${Math.min(100, (g.klaar / max) * 100)}%` }} /><i className="gepland" style={{ width: `${Math.min(100, (Math.max(0, ingepland - g.klaar) / max) * 100)}%` }} /></div>
                      <small>{agileWp ? `${fmtUren(ingepland)} u geschreven · ${fmtUren(g.klaar)} van ${fmtUren(g.uren)} u klaar` : g.w ? `${fmtUren(ingepland)} van ${fmtUren(g.uren)} uur ingepland` : `${fmtUren(ingepland)} uur`}</small>
                    </td>
                    <td>{bezigVandaag ? <span className="rap-status vandaag">● Vandaag bezig</span> : g.status ? <span className={`rap-status s-${g.status.replace(' ', '')}`}>{g.label}</span> : null}</td>
                    <td className="rap-volgende">{volgende}</td>
                    <td className="geen-print rap-pijl">{isOpen(g.key) ? '▴' : '▾'}</td>
                  </tr>
                  {isOpen(g.key) && (
                    <tr className="rap-detail">
                      <td colSpan={7}>{agileWp ? <AgileDetail tps={g.tps} naam={kort} /> : <KlassiekDetail taken={g.taken} tps={g.tps} vd={vd} naam={kort} />}</td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {!groepen.length && <tr><td colSpan={7} className="mute">Nog geen werkpakketten of taken.</td></tr>}
          </tbody>
        </table>
        <div className="rap-voetnoot">Uren = ingeplande uren van taken (bij agile: geschreven uren en de schatting van items op Klaar). Status volgens de werkpakketregels van bsmartr. Vandaag bezig = er loopt vandaag een taak.</div>
      </section>

      {/* 03 CIJFERS */}
      <Kop nr="03" titel="Het verloop in cijfers" />
      <div className="rap-twee">
        <section className="rap-blok">
          <div className="rap-label">Ingeplande uren per week</div>
          <div className="rap-staven">
            {r.weken.map((w) => {
              const tot = w.geweest + w.vandaag + w.komt;
              const deze = w.start === maandagVan(vd);
              return (
                <div key={w.start} className={`rap-staaf ${deze ? 'deze' : ''}`}>
                  <em>{tot ? fmtUren(tot) : ''}</em>
                  <div className="rap-staaf-vak" style={{ height: `${(tot / maxWeek) * 100}%` }}>
                    <i className="komt" style={{ flex: w.komt }} />
                    <i className="vandaag" style={{ flex: w.vandaag }} />
                    <i className="geweest" style={{ flex: w.geweest }} />
                  </div>
                  <b>W{isoWeek(w.start)}</b>
                  <span>{fmtDag(w.start, 'datum')}</span>
                </div>
              );
            })}
          </div>
          <div className="rap-legenda"><span><i className="geweest" />geweest</span><span><i className="vandaag" />vandaag</span><span><i className="komt" />nog te gaan</span></div>
        </section>
        <section className="rap-blok">
          <div className="rap-label">Uren per expertise</div>
          {r.prog.perExpertise.length ? (
            <div className="rap-expertises">
              {r.prog.perExpertise.map((x) => (
                <div key={x.expertise} className="rap-exp">
                  <div className="rap-exp-kop">
                    <b>{x.expertise}</b>
                    <span className={x.haalbaar === false ? 'rood' : x.haalbaar ? 'groen' : ''}>{x.rest <= 0 ? 'volledig ingepland' : x.haalbaar === false ? 'krap' : x.klaar ? `klaar ${fmtDag(x.klaar, 'datum')}` : ''}</span>
                  </div>
                  <div className="rap-balk"><i className="gepland" style={{ width: `${Math.min(100, x.budget ? (x.ingepland / x.budget) * 100 : 0)}%` }} /></div>
                  <small>{fmtUren(x.ingepland)} van {fmtUren(x.budget)} uur ingepland · nog {fmtUren(Math.max(0, x.rest))} uur · {x.team} engineer{x.team === 1 ? '' : 's'}</small>
                </div>
              ))}
            </div>
          ) : (
            <p className="mute">Er is geen budget per expertise vastgelegd.</p>
          )}
        </section>
      </div>

      {/* 04 AANDACHT */}
      <Kop nr="04" titel="Aandachtspunten" />
      <section className="rap-blok rap-aandacht">
        {punten.length ? (
          <div className="rap-punten">
            {punten.map((pt, i) => (
              <div key={i} className={`rap-punt ${pt.toon}`}>
                <span>{i + 1}</span>
                <div><b>{pt.titel}</b><p>{pt.tekst}</p></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rap-punt groen"><span>✓</span><div><b>Alles loopt volgens plan</b><p>Geen werkpakketten te laat, geen open terugmeldingen en de prognose valt voor de livegang.</p></div></div>
        )}
      </section>

      <footer className="rap-voet">
        <span>Vragen over dit project? Neem contact op met {p.pmNaam ?? 'de projectmanager'}{p.pmEmail ? ` (${p.pmEmail})` : ''}.</span>
        <span className="rap-powered">Stand {fmtDag(vd, 'jaar')} · powered by <img src="/logo.svg" alt="bsmartr" /></span>
      </footer>
    </div>
  );
}

function Kop({ nr, titel }: { nr: string; titel: string }) {
  return (
    <h2 className="rap-sectie"><span>{nr}</span>{titel}</h2>
  );
}

function Kpi({ label, getal, eenheid, tekst, balk, kleur, rood }: { label: string; getal: string; eenheid?: string; tekst: string; balk?: [number, number, number]; kleur: 'navy' | 'oranje' | 'groen' | 'rood'; rood?: boolean }) {
  return (
    <div className={`rap-kpi k-${kleur}`}>
      <div className="rap-label">{label}</div>
      <div className="rap-getal">{getal}{eenheid && <small> {eenheid}</small>}</div>
      <p className={rood ? 'rood' : ''}>{tekst}</p>
      {balk && (
        <div className="rap-balk">
          <i className="klaar" style={{ width: `${Math.min(100, (balk[0] / balk[2]) * 100)}%` }} />
          <i className="gepland" style={{ width: `${Math.min(100, (Math.max(0, balk[1] - balk[0]) / balk[2]) * 100)}%` }} />
        </div>
      )}
    </div>
  );
}

function regel(t: TaakRij, naam: (e?: string | null) => string, af: boolean, vd: string) {
  const bezig = !af && (t.startdatum ?? '') <= vd && (t.einddatum ?? '') >= vd;
  const meer = t.dagen > 1 ? ` · ${t.dagen} dagen` : '';
  return (
    <li key={t.id} className={bezig ? 'bezig' : ''}>
      <span className="rap-vink">{af ? (t.terugmelding === 'KLAAR' ? '✓' : t.terugmelding ? '!' : '·') : ''}</span>
      <b>{bezig ? 'Vandaag' : fmtDag(t.startdatum, 'kort')}</b>
      <span className="rap-titel">{t.titel}{bezig && <em> bezig</em>}{t.terugmelding === 'NIET_KLAAR' && <em className="rood"> niet klaar</em>}{t.terugmelding === 'HERPLANNEN' && <em className="rood"> herplannen</em>}{af && !t.terugmelding && t.taakType !== 'OVERLEG' && <em> wacht op terugmelding</em>}</span>
      <small>{t.van && t.tot ? `${t.van}–${t.tot}` : ''}{meer} · {naam(t.engineerId)} · {fmtUren(urenVan(t))} u</small>
    </li>
  );
}

function KlassiekDetail({ taken, tps, vd, naam }: { taken: TaakRij[]; tps: TakenpakketRij[]; vd: string; naam: (e?: string | null) => string }) {
  const gepland = taken.filter((t) => t.status === 'GEPLAND').sort((a, b) => (a.startdatum ?? '').localeCompare(b.startdatum ?? ''));
  const af = gepland.filter((t) => (t.einddatum ?? '') < vd);
  const komt = gepland.filter((t) => (t.einddatum ?? '') >= vd);
  const open = tps.map((tp) => ({ tp, rest: tp.uren - geplandeUren(taken.filter((t) => t.takenpakketId === tp.id)) })).filter((x) => x.rest > 0.01);
  const nietGepland = taken.filter((t) => t.status !== 'GEPLAND');
  return (
    <div className="rap-detail-grid">
      <div>
        <h4>Afgerond <small>{fmtUren(geplandeUren(af))} uur</small></h4>
        {af.length ? <ul>{af.map((t) => regel(t, naam, true, vd))}</ul> : <p className="mute">Nog geen taken geweest.</p>}
      </div>
      <div>
        <h4>Vandaag en nog te volgen <small>{fmtUren(geplandeUren(komt))} uur</small></h4>
        {komt.length ? <ul>{komt.map((t) => regel(t, naam, false, vd))}</ul> : <p className="mute">Geen taken meer gepland.</p>}
        {(open.length > 0 || nietGepland.length > 0) && (
          <>
            <h4 style={{ marginTop: 10 }}>Nog in te plannen</h4>
            <ul>
              {open.map(({ tp, rest }) => <li key={tp.id}><span className="rap-vink">○</span><b>{fmtUren(rest)} u</b><span className="rap-titel">{tp.naam}</span><small>{tp.expertise ?? ''}</small></li>)}
              {nietGepland.map((t) => <li key={t.id}><span className="rap-vink">○</span><b>taak</b><span className="rap-titel">{t.titel}</span><small>nog geen engineer of datum</small></li>)}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

function AgileDetail({ tps, naam }: { tps: TakenpakketRij[]; naam: (e?: string | null) => string }) {
  const per = (st: string[]) => tps.filter((t) => st.includes(t.status ?? 'BACKLOG'));
  const li = (t: TakenpakketRij, vink: string) => (
    <li key={t.id}><span className="rap-vink">{vink}</span><b>{ITEM_STATUS_LABEL[(t.status ?? 'BACKLOG') as keyof typeof ITEM_STATUS_LABEL]}</b><span className="rap-titel">{t.naam}</span><small>{t.eigenaarId ? naam(t.eigenaarId) : 'nog niet opgepakt'} · {fmtUren((t.besteedMin ?? 0) / 60)} van {fmtUren(t.uren)} u</small></li>
  );
  const backlog = per(['BACKLOG']);
  return (
    <div className="rap-detail-grid">
      <div>
        <h4>Klaar <small>{per(['KLAAR']).length} items</small></h4>
        {per(['KLAAR']).length ? <ul>{per(['KLAAR']).map((t) => li(t, '✓'))}</ul> : <p className="mute">Nog geen items klaar.</p>}
      </div>
      <div>
        <h4>In de sprint <small>{per(['GEPLAND', 'BEZIG', 'REVIEW']).length} items</small></h4>
        {per(['BEZIG', 'REVIEW', 'GEPLAND']).length ? <ul>{[...per(['BEZIG']), ...per(['REVIEW']), ...per(['GEPLAND'])].map((t) => li(t, ''))}</ul> : <p className="mute">Geen lopende sprint.</p>}
        {backlog.length > 0 && <p className="mute" style={{ marginTop: 8 }}>Backlog: {backlog.length} items, samen {fmtUren(backlog.reduce((s, t) => s + t.uren, 0))} uur geschat.</p>}
      </div>
    </div>
  );
}
